import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useTour } from '@reactour/tour';
import { TbDotsVertical } from 'react-icons/tb';
import { dayjs } from 'utils/providers/Timezone';
import { useFormikContext } from 'formik';
import { capitalize, compact, debounce } from 'lodash';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import { Button, DeleteItemModal, Status } from 'ui/components/common';
import {
  useDeleteTask,
  useGetDeal,
  useGetTask,
} from 'modules/account/sales/hooks';
import { NavAction, Tabs } from 'ui/components/nav';
import {
  Select,
  DateField,
  Textarea,
  SelectAvatar,
  TagsField,
} from 'ui/components/form';
import { useGetAccountUsers, useGetLeads } from 'modules/account/contact/hooks';
import { Avatar } from 'ui/components/img';
import { cn, useAdmin } from 'utils/helpers';
import { useGetSubscription } from 'modules/account/settings/hooks';
import c, { SIZE } from 'utils/constants';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import useModal from 'ui/partials/Modal/useModal';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { Attachment, Tag as ITag } from 'utils/types';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import Tag from 'ui/components/common/Tag';
import { Link } from 'react-router-dom';
import { MdClose } from 'react-icons/md';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import RichTextDisplay from 'ui/components/common/RichTextDisplay';

import { useTasksContext } from '../../containers/Tasks';
import getPriorityStyle from '../../utils/getPriorityStyle';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import { TASK_FORM } from '../schema';
import { DEMO_ACCOUNT_USER, DEMO_CONTACT } from '../../utils/constants';
import TaskAssociateDealModal from '../../components/TaskAssociateDealModal';

const { ACTIONS, PERMISSION_KEYS } = c;

type PROPS = FC<{
  mode?: 'popover' | 'modal';
  isLoading?: boolean;
  attachmentsToUpload: File[];
  setAttachmentsToUpload: React.Dispatch<React.SetStateAction<File[]>>;
}>;

const EditTaskForm: PROPS = ({
  mode = 'popover',
  isLoading = false,
  setAttachmentsToUpload,
  attachmentsToUpload = [],
}) => {
  const {
    values,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    errors,
    touched,
    resetForm,
  } = useFormikContext<TASK_FORM>();

  const canEdit = useCanRolePermissions(ACTIONS.UPDATE, PERMISSION_KEYS.TASKS);
  const canDelete = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.TASKS,
  );

  const { isAdmin } = useAdmin();
  const {
    state: { relativePath, isPreview },
  } = useAppWrapperContext();

  const [accountUserKeyword, setAccountUserKeyword] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('comments');
  const { isOpen, setCurrentStep } = useTour();

  const { selectedTaskId, clearTaskId } = useTasksContext();
  const { showModal } = useModal();
  const { data: task, isLoading: taskLoading } = useGetTask(
    selectedTaskId as string,
  );
  const { mutate: deleteTask, isLoading: deleteLoading } = useDeleteTask();
  const { data: subscription } = useGetSubscription();
  const { data: accountUsers } = useGetAccountUsers({
    keyword: accountUserKeyword,
  });
  const { data: leads } = useGetLeads();
  const { data: deal } = useGetDeal(values?.dealId ?? '', ['stage']);

  const debouncedSearch = debounce((string, setter) => setter(string), 800);

  const contactItems = useMemo(() => {
    if (isOpen) {
      return [DEMO_CONTACT] as any;
    }

    return (
      leads?.data.map((lead) => ({
        label: lead.fullName,
        value: lead.id,
      })) ?? []
    );
  }, [isOpen, leads?.data]);

  const accountUserItems = useMemo(
    () =>
      accountUsers?.data?.map((accountUser) => ({
        photoUrl: accountUser.photoUrl,
        itemLabel: accountUser.fullName,
        description: accountUser.emailAddress,
        value: accountUser._id,
      })) ?? ([] as AvatarOption[]),
    [accountUsers],
  );

  const handleDeleteDeal = useCallback(() => {
    deleteTask(task?._id ?? '', {
      onSuccess: clearTaskId,
    });
  }, [clearTaskId, deleteTask, task?._id]);

  const navOptions = useMemo(
    () =>
      compact([
        canEdit &&
          task?.status !== 'ARCHIVED' && {
            text: 'Edit',
            key: 'edit',
            onClick: () => {
              setIsEditing(true);
              if (isOpen) {
                setTimeout(() => setCurrentStep(7), 300);
              }
            },
            'data-tut': TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.EDIT_DRAFT,
          },
        canDelete && {
          text: 'Delete',
          key: 'delete',
          onClick: () => {
            showModal({
              title: 'Deal',
              content: (
                <DeleteItemModal
                  text="deal"
                  onClickDelete={handleDeleteDeal}
                  isLoading={deleteLoading}
                  validation
                />
              ),
            });
          },
        },
      ]),
    [
      canDelete,
      canEdit,
      deleteLoading,
      handleDeleteDeal,
      isOpen,
      setCurrentStep,
      showModal,
      task?.status,
    ],
  );

  const typeOptions = [
    {
      text: 'TODO',
      key: 'todo',
      onClick: () => {
        setFieldValue('type', 'TODO');
      },
      disabled: values.type === 'TODO',
    },
    {
      text: 'EMAIL',
      key: 'email',
      onClick: () => {
        setFieldValue('type', 'EMAIL');
      },
      disabled: values.type === 'EMAIL',
    },
    {
      text: 'CALL',
      key: 'call',
      onClick: () => {
        setFieldValue('type', 'CALL');
      },
      disabled: values.type === 'CALL',
    },
  ];

  useEffect(() => {
    setIsEditing(false);
  }, [selectedTaskId]);

  useEffect(() => {
    if (!isLoading) setIsEditing(false);
  }, [isLoading]);

  useEffect(() => {
    if (task) {
      setFieldValue('attachments', task.attachments);
    }
  }, [task, setFieldValue]);

  const handleCancel = () => {
    setIsEditing(false);
    setAttachmentsToUpload([]);
    resetForm();
  };

  const loadAccountUserItems = (
    search = '',
    cb: (options: AvatarOption[]) => void,
  ) => {
    debouncedSearch(search, setAccountUserKeyword);

    cb(accountUserItems);
  };

  const defaultAccountUsers = useMemo(() => {
    if (isOpen) {
      return [DEMO_ACCOUNT_USER];
    }

    return accountUserItems?.filter((i) =>
      values.accountUserId?.includes(i.value),
    );
  }, [accountUserItems, isOpen, values.accountUserId]);

  const leadLink = useMemo(() => {
    if (isPreview)
      return `/preview/${relativePath
        ?.split('/')
        .slice(2, relativePath?.split('/').length)
        .join('/')}contact/leads/${task?.lead?._id}`;

    return `/contact/leads/${task?.lead?._id}`;
  }, [relativePath, isPreview, task]);

  if (!task || taskLoading) {
    return <div />;
  }

  const handleOpenAssignDealModal = () => {
    showModal({
      title: 'Associate Deal',
      content: (
        <TaskAssociateDealModal
          handleSubmit={(dealId) => {
            setFieldValue('dealId', dealId);
          }}
        />
      ),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex flex-col justify-between',
        mode === 'popover'
          ? isAdmin
            ? 'h-[calc(100vh-122px)]'
            : 'h-[calc(100vh-100px)]'
          : 'min-h-[630px]',
      )}
    >
      <div
        className={cn(
          'p-[22px]',
          'flex-1',
          'space-y-[18px]',
          mode === 'popover' && 'overflow-y-auto overflow-x-hidden',
        )}
        data-tut={
          isEditing
            ? TOUR_SELECTOR.TOUR.SALES.TASK.FORM.FORM
            : TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.DETAILS
        }
      >
        {isEditing ? (
          <div className="relative">
            <NavAction
              label={
                <div className="flex h-6 items-center gap-x-2 text-sm font-semibold">
                  {values.type}
                  <HiOutlineChevronDown className="h-3 w-3" />
                </div>
              }
              options={typeOptions}
              optionsClassName="w-[160px] left-0 mt-1"
            />
            <input
              className="w-full border-b-2 border-secondary text-[22px] font-semibold outline-none"
              value={values.title}
              onChange={(e) => setFieldValue('title', e.target.value)}
              name="name"
            />
            <button
              type="button"
              onClick={() => setFieldValue('title', '')}
              className="absolute right-0 bottom-1.5 hover:text-secondary-base"
            >
              <MdClose className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold leading-[22px]">
                {task.type}
              </p>
              <p className="max-w-[400px] truncate text-[22px] font-semibold leading-7">
                {task.title}
              </p>
            </div>
            {!subscription?.isExpired && !!navOptions.length && (
              <NavAction
                label={
                  <TbDotsVertical
                    className={cn(
                      'h-5 w-5',
                      'text-secondary-dark hover:text-primary-base',
                    )}
                  />
                }
                options={navOptions}
                optionsClassName="w-[120px] right-0"
                data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.EDIT}
              />
            )}
          </div>
        )}
        <div className="grid grid-cols-3 items-center gap-y-4">
          <p className="col-span-1 text-sm">Status</p>
          <div className="col-span-2">
            {isEditing ? (
              <Select
                disabled={task.status === 'ARCHIVED'}
                options={[
                  { label: 'PENDING', value: 'PENDING' },
                  { label: 'IN PROGRESS', value: 'IN_PROGRESS' },
                  { label: 'COMPLETED', value: 'COMPLETED' },
                ]}
                onChange={(val) => {
                  setFieldValue('status', val.value);
                }}
                defaultValue={values.status}
              />
            ) : (
              <p
                className={cn(
                  'rounded-[6px] py-3 px-4 text-xs leading-none',
                  'w-fit font-semibold text-fill-tertiary',
                  task.status === 'COMPLETED'
                    ? 'bg-primary-base'
                    : task.status === 'IN_PROGRESS'
                      ? 'bg-[#1A3ED5]'
                      : 'bg-secondary-base',
                )}
              >
                {task.status === 'IN_PROGRESS' ? 'IN PROGRESS' : task.status}
              </p>
            )}
          </div>
          <p className="col-span-1 text-sm">Due Date</p>
          <div className="col-span-2">
            {isEditing ? (
              <DateField
                className="w-[276px]"
                selected={values.dueAt && new Date(values.dueAt)}
                onChange={(_, value) =>
                  setFieldValue('dueAt', new Date(value as Date))
                }
                placeholder="No Date"
                name="dueAt"
                size={SIZE.Small}
                clearSelected={() => setFieldValue('dueAt', null)}
              />
            ) : (
              <p className="text-sm">
                {task.dueAt
                  ? dayjs(task.dueAt).format(c.DATE_FORMAT_V2)
                  : 'Open deal'}
              </p>
            )}
          </div>
          <p className="col-span-1 text-sm">Priority</p>
          <div className="col-span-2">
            {isEditing ? (
              <Select
                textType={
                  errors.priority && touched.priority ? 'danger' : 'default'
                }
                subText={
                  errors.priority && touched.priority ? errors.priority : ''
                }
                onChange={(val) => {
                  setFieldValue('priority', val.value);
                }}
                options={[
                  { label: 'LOW', value: 'LOW' },
                  { label: 'MEDIUM', value: 'MEDIUM' },
                  { label: 'HIGH', value: 'HIGH' },
                ]}
                defaultValue={values.priority}
                size={SIZE.Medium}
                name="type"
                placeholder="Select"
                disabled={isSubmitting || deleteLoading}
                required
              />
            ) : (
              <Status
                label={capitalize(task.priority)}
                className={cn(
                  'w-fit p-0 px-2 py-[3px] text-xs leading-5',
                  getPriorityStyle(task?.priority),
                )}
              />
            )}
          </div>
          <p className="col-span-1 text-sm">
            Associated <br /> Contact
          </p>
          <div className="col-span-2">
            {isEditing ? (
              <Select
                defaultValue={values.leadId}
                textType={
                  errors.leadId && touched.leadId ? 'danger' : 'default'
                }
                subText={errors.leadId && touched.leadId ? errors.leadId : ''}
                onChange={(val) => {
                  setFieldValue('leadId', val.value);
                }}
                options={contactItems}
                size={SIZE.Medium}
                name="leadId"
                placeholder="Select"
                disabled={isSubmitting || deleteLoading}
              />
            ) : (
              <Link
                to={leadLink}
                className={cn('flex', 'items-center', 'gap-x-3', 'cursor')}
              >
                <Avatar user={task.lead} />
                <p className="text-sm font-semibold">{task.lead?.fullName}</p>
              </Link>
            )}
          </div>
          <p className="col-span-1 text-sm">Tags</p>
          <div className="col-span-2">
            {isEditing ? (
              <TagsField
                defaultValue={values.tagId?.map(
                  (tag) => (tag as unknown as ITag)._id,
                )}
                className="w-full"
                hideLabel
                size={c.SIZE.Small}
                onChange={(val) =>
                  setFieldValue(
                    'tagId',
                    val.map((tag) => tag.value),
                  )
                }
              />
            ) : (
              <div className="flex flex-1 gap-0.5 flex-wrap">
                {values?.tagId?.map((tag) => (
                  <Tag
                    key={(tag as unknown as ITag)._id}
                    label={(tag as unknown as ITag).tag}
                    size={c.SIZE.Small}
                  />
                ))}
              </div>
            )}
          </div>
          <p className="col-span-1 text-sm">Assigned To</p>
          <div className="col-span-2">
            {isEditing ? (
              <SelectAvatar
                {...(errors.accountUserId && { state: 'danger' })}
                className="w-full"
                loadOptions={loadAccountUserItems}
                subText={
                  errors.accountUserId && touched.accountUserId
                    ? (errors.accountUserId as string)
                    : undefined
                }
                onChange={(val) => {
                  setFieldValue(
                    'accountUserId',
                    (val as AvatarOption[]).map((v) => v.value),
                  );
                }}
                onClear={() => setFieldValue('accountUserId', [])}
                size={SIZE.Small}
                name="accountUserId"
                isMulti
                placeholder="Select"
                defaultOptions
                isDisabled={isSubmitting}
                defaultValue={defaultAccountUsers}
              />
            ) : (
              <AvatarGroup size={SIZE.Small} users={task.accountUsers} />
            )}
          </div>
          <p className="col-span-1 text-sm">Associated Deal</p>
          <div className="col-span-2">
            {isEditing ? (
              <div className="flex gap-2 items-center">
                {deal?._id && (
                  <div className="flex gap-2 items-center">
                    <div className="text-sm font-semibold">{deal?.name}</div>
                    <MdClose
                      className="w-4 h-4 hover:text-danger-base transition"
                      role="button"
                      onClick={() => {
                        setFieldValue('dealId', '');
                      }}
                    />
                  </div>
                )}
                {!values.dealId && (
                  <Button
                    intent={'tertiary'}
                    onClick={handleOpenAssignDealModal}
                    label="Associate Deal"
                  />
                )}
              </div>
            ) : !deal?._id ? (
              <Button
                intent={'tertiary'}
                onClick={handleOpenAssignDealModal}
                label="Associate Deal"
              />
            ) : (
              <Link
                className="text-sm font-semibold text-info-base"
                to={`/sales/deals/${deal?._id}`}
              >
                {deal?.name}
              </Link>
            )}
          </div>
        </div>
        <div className="pt-2.5">
          <p className="text-sm font-semibold">Description</p>
          <div className="mt-2">
            {isEditing ? (
              <Textarea
                richText
                value={values.content}
                onChange={(val) => setFieldValue('content', val)}
                disabled={isSubmitting || deleteLoading}
              />
            ) : (
              <RichTextDisplay text={task.content} />
            )}
          </div>
        </div>
        <div className=" border-b-2 border-b-separator-secondary"></div>
        <Tabs
          selectedTab={selectedTab}
          size={SIZE.Small}
          className="w-full"
          items={[
            { label: 'Comments', value: 'comments' },
            { label: 'Attachments', value: 'attachments' },
          ]}
          onChange={({ value: tab }) => setSelectedTab(tab)}
          type="secondary"
        />
        {selectedTab === 'attachments' && (
          <TaskAttachments
            isEditing={isEditing}
            isLoading={isSubmitting || deleteLoading}
            attachments={values.attachments as Attachment[]}
            addAttachmentToUpload={setAttachmentsToUpload}
            attachmentsToUpload={attachmentsToUpload}
            taskId={task._id}
          />
        )}
        {selectedTab === 'comments' && <TaskComments taskId={task._id} />}
      </div>
      {isEditing && (
        <div
          className={cn(
            'flex justify-end gap-3',
            'p-4 text-right',
            'bg-fill-tertiary',
            'border-t-[0.5px]',
            'border-t-[#DCEDF8]',
          )}
        >
          <Button
            data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.MANAGE.EDIT_BUTTON}
            type="submit"
            disabled={isLoading || isSubmitting || deleteLoading}
            label="SAVE"
          />
          <Button
            intent="tertiary"
            onClick={handleCancel}
            disabled={isLoading || deleteLoading}
            label="CANCEL"
          />
        </div>
      )}
    </form>
  );
};

export default EditTaskForm;
